import {
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useFoodChallengeDetails } from '../../../api/healthCoach/useFoodChallengeDetails';
import { Loader } from '../../Loader';
import type { FoodChallengeBlockData } from '../../../models/foodChallengeBlockData';
import { formatDateInBrowserTZ } from '../utils';
import { useFetchFoodChallenges } from 'api/healthCoach/useFetchFoodChallenges';

const getChallengeDateRangeLabel = (
  challenge: FoodChallengeBlockData
): string => {
  const startedOn = formatDateInBrowserTZ(
    challenge.created_at ?? undefined,
    'MMM D'
  );

  if (!challenge.ended_on) return startedOn;

  const endedOn = formatDateInBrowserTZ(challenge.ended_on, 'MMM D');

  return `${startedOn}-${endedOn}`;
};

const getSymptomsListLabel = (symptomsList: string[]): string =>
  symptomsList.length ? symptomsList.join(', ') : '--';

const getFoodChallengeName = (challenge: FoodChallengeBlockData): string => {
  const categoryName =
    challenge.food_category?.display_name || challenge.food_category?.name;
  const foodItemName = challenge.food_item?.name;

  if (categoryName && foodItemName) return `${categoryName}: ${foodItemName}`;
  if (foodItemName) return foodItemName;
  if (categoryName) return categoryName;

  return 'Food challenge';
};

type Props = {
  blockId?: string;
};

export const FoodChallengeDetailsContent = ({ blockId }: Props) => {
  const { patient = '' } = useParams();
  const [selectedFoodChallengeId, setSelectedFoodChallengeId] = useState<
    string | null
  >(null);
  const { activeFoodChallenges, isLoading: foodChallengesLoading } =
    useFetchFoodChallenges({
      userBlockId: blockId,
    });

  const { foodChallengeDetails, isLoading, isError } = useFoodChallengeDetails({
    enabled: !!selectedFoodChallengeId,
    foodChallengeId: selectedFoodChallengeId ?? '',
    userId: patient,
  });

  if (foodChallengesLoading) {
    return <Loader loading />;
  }

  if (!activeFoodChallenges.length) {
    return (
      <Text color={'blueGray.600'} fontSize={'sm'}>
        No challenged foods available for this block.
      </Text>
    );
  }

  return (
    <VStack
      bgColor={'feather.100'}
      borderColor={'cloud.200'}
      borderRadius={'xl'}
      borderWidth={1}
      overflow={'hidden'}
    >
      {activeFoodChallenges.map((challenge, index) => {
        const challengeName = getFoodChallengeName(challenge);
        const challengedOn = getChallengeDateRangeLabel(challenge);
        const isSelected = selectedFoodChallengeId === challenge.id;

        return (
          <VStack
            key={challenge.id}
            borderBottomWidth={index < activeFoodChallenges.length - 1 ? 1 : 0}
            borderColor={'cloud.200'}
            px={4}
            py={3}
            gap={3}
          >
            <Pressable
              w="100%"
              onClick={() => {
                setSelectedFoodChallengeId((prev) =>
                  prev === challenge.id ? null : challenge.id
                );
              }}
            >
              <HStack alignItems={'center'} justifyContent={'space-between'}>
                <Text
                  color={'secondary.500'}
                  fontSize={'md'}
                  fontWeight={'medium'}
                >
                  {challengeName}
                </Text>

                <Text
                  color={'secondary.200'}
                  fontSize={'sm'}
                  fontWeight={'medium'}
                >
                  {challengedOn}
                </Text>
              </HStack>
            </Pressable>

            {isSelected && (
              <Box bgColor={'white'} borderRadius={'md'} px={3} py={3}>
                {isLoading && <Loader loading />}

                {!isLoading && isError && (
                  <Text color={'error.600'} fontSize={'sm'}>
                    We couldn't load food challenge details. Please try again.
                  </Text>
                )}

                {!isLoading && !isError && foodChallengeDetails && (
                  <VStack gap={2}>
                    <Text color={'secondary.500'} fontSize={'sm'}>
                      Step:{' '}
                      <Text as="span" fontWeight={'bold'}>
                        {foodChallengeDetails.step || '--'}
                      </Text>
                    </Text>

                    <Text color={'secondary.500'} fontSize={'sm'}>
                      Current day:{' '}
                      <Text as="span" fontWeight={'bold'}>
                        {`${foodChallengeDetails.current_day ?? '--'}`}
                      </Text>
                    </Text>

                    <Text color={'secondary.500'} fontSize={'sm'}>
                      System insight:{' '}
                      <Text as="span" fontWeight={'bold'}>
                        {foodChallengeDetails.system_insight || '--'}
                      </Text>
                    </Text>

                    <Text color={'secondary.500'} fontSize={'sm'}>
                      User insight:{' '}
                      <Text as="span" fontWeight={'bold'}>
                        {foodChallengeDetails.user_insight || '--'}
                      </Text>
                    </Text>

                    {!!foodChallengeDetails?.days?.length && (
                      <VStack pt={1} gap={1}>
                        <Text
                          color={'secondary.500'}
                          fontSize={'sm'}
                          fontWeight={'bold'}
                        >
                          Challenge Days
                        </Text>

                        <VStack pl={3} gap={3}>
                          {foodChallengeDetails.days.map((day) => (
                            <VStack key={day.id} gap={1}>
                              <Text
                                color={'secondary.500'}
                                fontSize={'sm'}
                                fontWeight={'bold'}
                              >
                                Day {day.day}:
                              </Text>

                              <Text color={'secondary.500'} fontSize={'sm'}>
                                Ate at:{' '}
                                <Text as="span" fontWeight={'bold'}>
                                  {formatDateInBrowserTZ(
                                    day.ate_at ?? undefined
                                  )}
                                </Text>
                              </Text>

                              <Text color={'secondary.500'} fontSize={'sm'}>
                                Serving size:{' '}
                                <Text as="span" fontWeight={'bold'}>
                                  {`${day.serving_size ?? '--'}`}
                                </Text>
                              </Text>

                              <Text color={'secondary.500'} fontSize={'sm'}>
                                Symptoms at:{' '}
                                <Text as="span" fontWeight={'bold'}>
                                  {formatDateInBrowserTZ(
                                    day.symptoms_at ?? undefined
                                  )}
                                </Text>
                              </Text>

                              <Text color={'secondary.500'} fontSize={'sm'}>
                                Symptoms experienced:{' '}
                                <Text as="span" fontWeight={'bold'}>
                                  {getSymptomsListLabel(day.symptoms_list)}
                                </Text>
                              </Text>

                              <Text color={'secondary.500'} fontSize={'sm'}>
                                User sentiment:{' '}
                                <Text as="span" fontWeight={'bold'}>
                                  {day.user_sentiment || '--'}
                                </Text>
                              </Text>
                            </VStack>
                          ))}
                        </VStack>
                      </VStack>
                    )}
                  </VStack>
                )}
              </Box>
            )}
          </VStack>
        );
      })}
    </VStack>
  );
};
